import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Textarea } from '../ui/textarea'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useIsPlatformAdmin } from '../../hooks/useAuth'
import { useMyOrganizations } from '../../api/multiOrg'
import {
    useManageFeatureFlags,
    useFeatureFlagMutations,
    type FeatureFlagInfo,
} from '../../hooks/use-feature-flags'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import { mdiFlagOutline } from '@mdi/js'

const ADMIN_ROLES: Set<string> = new Set(['admin', 'owner'])

interface FeatureFlagsManagementPageProps {
    org: string
}

type Notice = { kind: 'success' | 'error'; text: string } | null

export function FeatureFlagsManagementPage({ org }: FeatureFlagsManagementPageProps) {
    const { t } = useTranslation('feature-flags')

    const breadcrumbs = useMemo(
        () => [
            { title: t('common:nav.home'), path: `/${org}` },
            { title: t('settings:page.title'), path: `/${org}/settings` },
            { title: t('page.title') },
        ],
        [org, t]
    )
    useBreadcrumbs(breadcrumbs)

    const isPlatformAdmin = useIsPlatformAdmin()
    const { data: myOrgs } = useMyOrganizations()
    const myRole = useMemo(
        () => myOrgs?.data?.find((o) => o.code === org)?.role?.code,
        [myOrgs, org]
    )
    const isOrgAdmin = isPlatformAdmin || (myRole ? ADMIN_ROLES.has(myRole) : false)

    const { data, isLoading, isError } = useManageFeatureFlags(org)
    const [notice, setNotice] = useState<Notice>(null)

    if (!isOrgAdmin) {
        return (
            <div className="container mx-auto py-8 max-w-4xl" data-testid="feature-flags-page">
                <PageHeader icon={SECTION_ICONS.featureFlags} title={t('page.title')} />
                <Alert variant="destructive" className="mt-4">
                    <AlertTitle>{t('access.denied_title')}</AlertTitle>
                    <AlertDescription>{t('access.denied_description')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8 max-w-4xl" data-testid="feature-flags-page">
            <PageHeader
                className="mb-6"
                icon={SECTION_ICONS.featureFlags}
                title={t('page.title')}
                description={t('page.subtitle')}
            />

            {notice && (
                <Alert
                    variant={notice.kind === 'error' ? 'destructive' : 'default'}
                    className="mb-4"
                    data-testid="feature-flags-notice"
                >
                    <AlertTitle>
                        {notice.kind === 'error' ? t('notice.error_title') : t('notice.success_title')}
                    </AlertTitle>
                    <AlertDescription>{notice.text}</AlertDescription>
                </Alert>
            )}

            {isLoading && (
                <p className="text-muted-foreground" data-testid="feature-flags-loading">
                    {t('states.loading')}
                </p>
            )}
            {isError && (
                <Alert variant="destructive" data-testid="feature-flags-error">
                    <AlertTitle>{t('notice.error_title')}</AlertTitle>
                    <AlertDescription>{t('states.error')}</AlertDescription>
                </Alert>
            )}

            {!isLoading && !isError && (
                <div className="bg-card rounded-lg border border-border divide-y divide-border">
                    {(data?.flags ?? []).map((flag) => (
                        <FeatureFlagRow
                            key={flag.code}
                            org={org}
                            flag={flag}
                            isPlatformAdmin={isPlatformAdmin}
                            onNotice={setNotice}
                        />
                    ))}
                    {(data?.flags ?? []).length === 0 && (
                        <EmptyState
                            variant="inline"
                            icon={mdiFlagOutline}
                            title={t('states.empty')}
                            data-testid="feature-flags-empty"
                        />
                    )}
                </div>
            )}
        </div>
    )
}

function stageVariant(stage: string): 'secondary' | 'info' | 'success' {
    switch (stage) {
        case 'server':
            return 'success'
        case 'org':
            return 'info'
        default:
            return 'secondary'
    }
}

function sourceVariant(source: string): 'secondary' | 'info' | 'success' {
    switch (source) {
        case 'organization':
            return 'success'
        case 'server':
        case 'template':
            return 'info'
        default:
            return 'secondary'
    }
}

function isBoolean(v: unknown): v is boolean {
    return typeof v === 'boolean'
}

/**
 * Extracts a human-readable, per-violation detail string from a setting/flag
 * write error. A schema-validation failure (setting_value_invalid /
 * setting_schema_invalid, spec 2026-07-04-11) is shaped
 * `{ title, code?, fields: [{name, message}] }` (base.ValidationError /
 * WriteValidationErrorWithCode on the Go side — same shape as
 * site-map/hooks.ts's footprintErrorInfo documents for other 422s in this
 * codebase). Returns undefined for a non-validation error (e.g. a plain
 * network failure, or a 403/404) so the caller falls back to its generic
 * i18n'd message.
 */
export function settingErrorDetail(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') return undefined
    const e = error as { fields?: Array<{ name?: string; message?: string }> }
    if (!Array.isArray(e.fields) || e.fields.length === 0) return undefined
    return e.fields
        .map((f) => (f.name ? `${f.name}: ${f.message ?? ''}` : (f.message ?? '')))
        .filter(Boolean)
        .join('; ')
}

interface RowProps {
    org: string
    flag: FeatureFlagInfo
    isPlatformAdmin: boolean
    onNotice: (n: Notice) => void
}

function FeatureFlagRow({ org, flag, isPlatformAdmin, onNotice }: RowProps) {
    const { t } = useTranslation('feature-flags')
    const { setOverride, clearOverride } = useFeatureFlagMutations(org)

    // A superadmin-stage flag may only be written by a platform admin.
    const canWrite = flag.stage !== 'superadmin' || isPlatformAdmin

    const [editing, setEditing] = useState(false)
    const [jsonText, setJsonText] = useState('')
    const [jsonError, setJsonError] = useState<string | null>(null)
    const [confirmClear, setConfirmClear] = useState(false)

    useEffect(() => {
        // Reset the editor when the resolved value changes.
        setJsonText(JSON.stringify(flag.resolved_value, null, 2))
    }, [flag.resolved_value])

    const boolValue = isBoolean(flag.resolved_value) ? flag.resolved_value : null

    const handleToggle = async () => {
        if (boolValue === null) return
        try {
            await setOverride.mutateAsync({ code: flag.code, value: !boolValue })
            onNotice({ kind: 'success', text: t('notice.saved', { code: flag.code }) })
        } catch (err) {
            const detail = settingErrorDetail(err)
            onNotice({
                kind: 'error',
                text: detail
                    ? t('notice.save_failed_detail', { code: flag.code, detail })
                    : t('notice.save_failed', { code: flag.code }),
            })
        }
    }

    const handleSaveJson = async () => {
        let parsed: unknown
        try {
            parsed = JSON.parse(jsonText)
        } catch {
            setJsonError(t('editor.invalid_json'))
            return
        }
        setJsonError(null)
        try {
            await setOverride.mutateAsync({ code: flag.code, value: parsed })
            onNotice({ kind: 'success', text: t('notice.saved', { code: flag.code }) })
            setEditing(false)
        } catch (err) {
            const detail = settingErrorDetail(err)
            onNotice({
                kind: 'error',
                text: detail
                    ? t('notice.save_failed_detail', { code: flag.code, detail })
                    : t('notice.save_failed', { code: flag.code }),
            })
        }
    }

    const handleClear = async () => {
        try {
            await clearOverride.mutateAsync({ code: flag.code })
            onNotice({ kind: 'success', text: t('notice.cleared', { code: flag.code }) })
        } catch (err) {
            const detail = settingErrorDetail(err)
            onNotice({
                kind: 'error',
                text: detail
                    ? t('notice.clear_failed_detail', { code: flag.code, detail })
                    : t('notice.clear_failed', { code: flag.code }),
            })
        } finally {
            setConfirmClear(false)
        }
    }

    return (
        <div className="px-4 py-4" data-testid={`feature-flag-row-${flag.code}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground" data-testid={`feature-flag-code-${flag.code}`}>
                            {flag.code}
                        </span>
                        <Badge variant={stageVariant(flag.stage)} data-testid={`feature-flag-stage-${flag.code}`}>
                            {t(`stage.${flag.stage}`)}
                        </Badge>
                        <Badge variant={sourceVariant(flag.source)} data-testid={`feature-flag-source-${flag.code}`}>
                            {t(`source.${flag.source}`)}
                        </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{flag.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                        {t('row.value')}:{' '}
                        <code className="text-foreground" data-testid={`feature-flag-value-${flag.code}`}>
                            {JSON.stringify(flag.resolved_value)}
                        </code>
                    </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {boolValue !== null ? (
                        <Button
                            variant={boolValue ? 'default' : 'outline'}
                            size="sm"
                            disabled={!canWrite || setOverride.isPending}
                            onClick={handleToggle}
                            aria-label={t('actions.toggle')}
                            title={t('actions.toggle')}
                            data-testid={`feature-flag-toggle-${flag.code}`}
                        >
                            {boolValue ? t('row.on') : t('row.off')}
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={!canWrite}
                            onClick={() => setEditing((e) => !e)}
                            data-testid={`feature-flag-edit-${flag.code}`}
                        >
                            {editing ? t('actions.cancel') : t('actions.edit')}
                        </Button>
                    )}
                    {flag.has_org_override && (
                        <Button
                            variant="destructive"
                            size="sm"
                            disabled={!canWrite || clearOverride.isPending}
                            onClick={() => setConfirmClear(true)}
                            data-testid={`feature-flag-clear-${flag.code}`}
                        >
                            {t('actions.clear')}
                        </Button>
                    )}
                </div>
            </div>

            {editing && boolValue === null && (
                <div className="mt-3" data-testid={`feature-flag-editor-${flag.code}`}>
                    <Textarea
                        value={jsonText}
                        onChange={(e) => setJsonText(e.target.value)}
                        rows={4}
                        className="font-mono text-xs"
                        data-testid={`feature-flag-json-${flag.code}`}
                    />
                    {jsonError && (
                        <p className="text-xs text-destructive mt-1" data-testid={`feature-flag-json-error-${flag.code}`}>
                            {jsonError}
                        </p>
                    )}
                    <div className="flex gap-2 mt-2">
                        <Button
                            size="sm"
                            disabled={setOverride.isPending}
                            onClick={handleSaveJson}
                            data-testid={`feature-flag-save-${flag.code}`}
                        >
                            {t('actions.save')}
                        </Button>
                    </div>
                </div>
            )}

            <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('clear_dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('clear_dialog.description', { code: flag.code })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`feature-flag-clear-cancel-${flag.code}`}>
                            {t('actions.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleClear}
                            data-testid={`feature-flag-clear-confirm-${flag.code}`}
                        >
                            {t('actions.clear')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
